import { Contact, WebViewMessageData } from 'aven_types'
import {
    canGetWhetherAppCanAskForContactPermissionAgain,
    canGetWhetherAppCanAskPushPermissionAgain,
    canReceiveSessionInfoObject,
    canReturnPushPermissionResponse,
    canSendVueLoadedMessage,
    getIsSetSafeAreaPaddingAvailable,
    isCheckPushPermissionEnabled,
} from '@/nativeWebInteraction/nativeFeatureAvailability'
import { log } from '@/utils/logger'
import { Optional, WebViewMessageEventName } from 'aven_types'
import { appRoutePaths } from '@/routes/appRoutes'
import assert from 'assert'

export const getStoredAccessToken = (): string | null | undefined => {
    // If the user is logged in (i.e. the native app has an access token) the web
    // will receive it at this window variable. See aven_apps/aven_score/src/Web/index.js
    return window.accessToken
}

export const getAppInstallId = (): string | null | undefined => {
    // If the Aven Advisor website is running in a native shell, the shell will set
    // the installId at this window variable. See aven_apps/aven_score/src/Web/index.js
    return window.installId
}

export const isRunningInNativeApp = (): boolean => {
    // If the web site is running in the React Native Advisor app, we will have exposed the interface
    // for the web to communicate back to it. See:
    // https://github.com/react-native-webview/react-native-webview/blob/master/docs/Guide.md#the-windowreactnativewebviewpostmessage-method-and-onmessage-prop
    // Note that comment below silences lint; ReactNativeWebView will be available when running in React Native app
    const isRunningInNativeApp = !!window.ReactNativeWebView
    log.info('Is Aven Advisor web running in native app? %{isRunningInNativeApp}', { isRunningInNativeApp })
    return isRunningInNativeApp
}

enum PlatformOs {
    ios = 'ios',
    android = 'android',
}

export const isIosNativeApp = (): boolean => {
    return isRunningInNativeApp() && window.platformOs === PlatformOs.ios
}

export const isAndroidNativeApp = (): boolean => {
    return isRunningInNativeApp() && window.platformOs === PlatformOs.android
}

const sendMessageToNativeApp = (dataToSend: WebViewMessageData) => {
    const stringData = JSON.stringify(dataToSend)
    if (window.ReactNativeWebView) {
        log.info('Sending a message from Advisor web to app: %{stringData}', { stringData })
        // Note that comment below silences lint; ReactNativeWebView will be available when running in React Native app
        window.ReactNativeWebView.postMessage(stringData)
    } else {
        log.info('Aven Advisor not running in native shell, so not sending message to native app: %{stringData}', { stringData })
    }
}

/**
 * A copy of PermissionResponse in aven_apps/aven_score/node_modules/expo-modules-core/src/PermissionsInterface.ts
 * This is what React Native sends back to the web when we request permission.
 */
export interface PermissionResponse {
    /**
     * Determines the status of the permission.
     */
    status: PermissionStatus
    /**
     * Determines time when the permission expires. Currently, all permissions are granted permanently.
     */
    expires: 'never' | number
    /**
     * A convenience boolean that indicates if the permission is granted.
     */
    granted: boolean
    /**
     * Indicates if user can be asked again for specific permission. If not, one should be directed to the Settings app in order to enable/disable the permission.
     */
    canAskAgain: boolean
}

export const requestPushPermission = async (): Promise<PermissionResponse | undefined> => {
    log.info('Aven Advisor web is requesting push permission')
    return new Promise<PermissionResponse | undefined>((resolve, reject) => {
        try {
            if (canReturnPushPermissionResponse()) {
                log.info('Aven Advisor native indicates it can return push permission response, so web is setting up to receive that')
                window.receivePushPermissionResponse = (permissionResponse: PermissionResponse) => {
                    log.info('Aven Advisor web received push notif permission response from native: %{permissionResponseJson}', { permissionResponseJson: JSON.stringify(permissionResponse) })
                    resolve(permissionResponse)
                }
                sendMessageToNativeApp({
                    eventName: WebViewMessageEventName.REQUEST_PUSH_PERMISSION,
                    windowCallbackMethodName: 'receivePushPermissionResponse',
                })
            } else {
                log.info('Aven Advisor native does not indicate it can return push permission response, so web is immediately resolving the requestPushPermission promise')
                sendMessageToNativeApp({
                    eventName: WebViewMessageEventName.REQUEST_PUSH_PERMISSION,
                })
                resolve(undefined)
            }
        } catch (e) {
            reject(e)
        }
    }).finally(() => {
        window.receivePushPermissionResponse = undefined
    })
}

export const getPushAlreadyEnabled = async (): Promise<boolean> => {
    log.info('Aven Advisor web is getting whether push is already enabled')
    if (!isCheckPushPermissionEnabled()) {
        return false
    }
    log.info('Getting current native push permissions')
    return new Promise<boolean>((resolve, reject) => {
        try {
            window.receivePushEnabled = (pushPayload: { pushEnabled: boolean }) => {
                log.info('Got native push permissions, push enabled: %{pushEnabled}', { pushEnabled: pushPayload.pushEnabled })
                resolve(pushPayload.pushEnabled)
            }
            sendMessageToNativeApp({
                eventName: WebViewMessageEventName.GET_PUSH_ENABLED,
                windowCallbackMethodName: 'receivePushEnabled',
            })
        } catch (e) {
            reject(e)
        }
    }).finally(() => {
        window.receivePushEnabled = undefined
    })
}

export const trySendAppsFlyerSignupEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer signup event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_SIGN_UP_EVENT })
}

export const trySendAppsFlyerHomeownerSignupEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer homeowner signup event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_HOMEOWNER_SIGN_UP_EVENT })
}

export const trySendAppsFlyerHelocSignupEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer HELOC signup event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_HELOC_SIGN_UP_EVENT })
}

export const trySendAppsFlyerCreditCardSignupEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer credit card signup event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_CREDIT_CARD_SIGN_UP_EVENT })
}

export const trySendAppsFlyerPhoneNumberSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer phone number submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_PHONE_NUMBER_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerOtpSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer OTP submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_OTP_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerSsnSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer SSN submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_SSN_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerPiiSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer PII submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_PII_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerKbaSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer KBA success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_KBA_SUCCESS_EVENT })
}

export const trySendAppsFlyerNotificationPermissionGrantedEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer notification permission granted event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_NOTIFICATION_PERMISSION_GRANTED_EVENT })
}

export const trySendAppsFlyerStatedGoalSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer stated goal submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_STATED_GOAL_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerStatedGoalBudgetSpendTrackingSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer stated goal budget spend tracking submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_STATED_GOAL_BUDGET_SPEND_TRACKING_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerStatedGoalLienReportSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer stated goal lien report submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_STATED_GOAL_LIEN_REPORT_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerStatedGoalFindSubscriptionsSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer stated goal find subscriptions submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_STATED_GOAL_FIND_SUBSCRIPTIONS_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerStatedGoalCreditScoreSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer stated goal credit score submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_STATED_GOAL_CREDIT_SCORE_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerStatedGoalOtherSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer stated goal other submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_STATED_GOAL_OTHER_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerHomeownerPhoneNumberSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer homeowner phone number submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_HOMEOWNER_PHONE_NUMBER_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerHomeownerOtpSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer homeowner OTP submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_HOMEOWNER_OTP_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerHomeownerSsnSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer homeowner SSN submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_HOMEOWNER_SSN_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerHomeownerPiiSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer homeowner PII submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_HOMEOWNER_PII_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerHomeownerKbaSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer homeowner KBA success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_HOMEOWNER_KBA_SUCCESS_EVENT })
}

export const trySendAppsFlyerHomeownerNotificationPermissionGrantedEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer homeowner notification permission granted event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_HOMEOWNER_NOTIFICATION_PERMISSION_GRANTED_EVENT })
}

export const trySendAppsFlyerHomeownerStatedGoalSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer homeowner stated goal submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_HOMEOWNER_STATED_GOAL_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerHomeownerStatedGoalBudgetSpendTrackingSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer homeowner stated goal budget spend tracking submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_HOMEOWNER_STATED_GOAL_BUDGET_SPEND_TRACKING_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerHomeownerStatedGoalLienReportSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer homeowner stated goal lien report submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_HOMEOWNER_STATED_GOAL_LIEN_REPORT_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerHomeownerStatedGoalFindSubscriptionsSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer homeowner stated goal find subscriptions submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_HOMEOWNER_STATED_GOAL_FIND_SUBSCRIPTIONS_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerHomeownerStatedGoalCreditScoreSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer homeowner stated goal credit score submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_HOMEOWNER_STATED_GOAL_CREDIT_SCORE_SUBMIT_SUCCESS_EVENT })
}

export const trySendAppsFlyerHomeownerStatedGoalOtherSubmitSuccessEvent = () => {
    log.info('Aven Advisor web is trying to send AppsFlyer homeowner stated goal other submit success event')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.TRY_SEND_APPSFLYER_HOMEOWNER_STATED_GOAL_OTHER_SUBMIT_SUCCESS_EVENT })
}

export const requestTrackingPermission = () => {
    log.info('Aven Advisor web is requesting tracking permission')
    sendMessageToNativeApp({ eventName: WebViewMessageEventName.REQUEST_TRACKING_PERMISSION })
}

export const saveAccessTokenOnNative = (accessToken: string | null) => {
    log.info('Aven Advisor web is trying to save access token on native: %{accessToken}', { accessToken })
    window.accessToken = accessToken
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.SAVE_ACCESS_TOKEN,
        payload: accessToken,
    })
}

export const saveSessionInfoOnNative = (accessToken: string | null, sessionId: string | null) => {
    log.info('Aven Advisor web is trying to save session access token on native: %{accessToken}', { accessToken })
    if (canReceiveSessionInfoObject()) {
        sendMessageToNativeApp({
            eventName: WebViewMessageEventName.SAVE_SESSION_INFO,
            payload: {
                accessToken,
                sessionId,
            },
        })
    }
}

export const openUrlInBrowser = (url: string) => {
    log.info('Aven Advisor web is trying to open URL in browser: %{url}', { url })
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.OPEN_URL_IN_BROWSER,
        payload: {
            url,
        },
    })
}

// Keep in sync with DocumentType in aven_apps/aven_score/src/downloadManager/downloadManager.ts
// See aven_backend/src/util/legalConstants.ts for all possible types
// that could potentially get added here.
export enum DocumentType {
    creditScoreDisclosure = 'CreditScoreDisclosure',
    earlyHELOCDisclosure = 'EarlyHELOCDisclosure',
    pricingAndTerms = 'PricingAndTerms',
    adverseActionNotice = 'AdverseActionNotice',
    uccAccountAgreementSigned = 'uccAccountAgreementSigned',
    termsOfService = 'TermsOfService',
}

// Keep in sync with SaveAndShareDocumentPayload in aven_apps/aven_score/src/nativeWebInteraction/nativeWebInteraction.ts
interface SaveAndShareDocumentPayload {
    docType: DocumentType
}

export const saveAndShareDocument = ({ docType }: SaveAndShareDocumentPayload) => {
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.SAVE_AND_SHARE_DOCUMENT,
        payload: {
            docType,
        },
    })
}

// Copy of aven_apps/aven_score/node_modules/expo-modules-core/src/PermissionsInterface.ts
export enum PermissionStatus {
    GRANTED = 'granted',
    UNDETERMINED = 'undetermined',
    DENIED = 'denied',
}

// Keep in sync with ContactsPayload in aven_apps/aven_score/src/nativeWebInteraction/nativeWebInteraction.ts
export interface ContactsPayload {
    contactsApiAvailable: boolean
    permissionStatus: PermissionStatus
    canAskPermissionAgain?: boolean
    contacts: Contact[]
}

export const retrieveContacts = async (): Promise<ContactsPayload> => {
    return new Promise<ContactsPayload>((resolve, reject) => {
        try {
            window.receiveContacts = (contactPayload: ContactsPayload) => {
                resolve(contactPayload)
            }
            sendMessageToNativeApp({
                eventName: WebViewMessageEventName.RETRIEVE_CONTACTS,
                windowCallbackMethodName: 'receiveContacts',
            })
        } catch (e) {
            reject(e)
        }
    }).finally(() => {
        window.receiveContacts = undefined
    })
}

export const setLastRequestedTimeForNotifPermission = () => {
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.SET_NOTIF_PERM_ASKED_TIME,
    })
}

export const getLastRequestedTimeForNotifPermission = async (): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
        try {
            window.receiveLastRequestedPushNotificationDate = (lastRequestedPushNotificationDatePayload: { lastRequestedPushNotificationDate: string }) => {
                resolve(lastRequestedPushNotificationDatePayload.lastRequestedPushNotificationDate)
            }
            sendMessageToNativeApp({
                eventName: WebViewMessageEventName.GET_NOTIF_PERM_ASKED_TIME,
                windowCallbackMethodName: 'receiveLastRequestedPushNotificationDate',
            })
        } catch (e) {
            reject(e)
        }
    }).finally(() => {
        window.receiveLastRequestedPushNotificationDate = undefined
    })
}

export const getCanAskForPushPermissionAgain = async (defaultValue: boolean): Promise<boolean> => {
    return new Promise<boolean>((resolve, reject) => {
        try {
            if (canGetWhetherAppCanAskPushPermissionAgain()) {
                log.info('Aven Advisor native indicates it can return whether we can ask for push permission again, so web is setting up to receive that')
                window.receiveCanAskPushAgain = (canAskPushAgainPayload: { canAskPushAgain: boolean }) => {
                    log.info('Aven Advisor web received whether it can request push notif permission again from native: %{canAskPushAgainPayloadJson}', {
                        canAskPushAgainPayloadJson: JSON.stringify(canAskPushAgainPayload),
                    })
                    resolve(canAskPushAgainPayload.canAskPushAgain)
                }
                sendMessageToNativeApp({
                    eventName: WebViewMessageEventName.GET_CAN_ASK_PUSH_AGAIN,
                    windowCallbackMethodName: 'receiveCanAskPushAgain',
                })
            } else {
                log.info('Aven Advisor native indicates it cannot return whether we can ask for push permission again, so web is using the default value of %{defaultValue}', { defaultValue })
                resolve(defaultValue)
            }
        } catch (e) {
            reject(e)
        }
    }).finally(() => {
        window.receiveCanAskPushAgain = undefined
    })
}

export const canAskForContactPermissionAgain = async (): Promise<{ canAskAgain: boolean; permissionStatus: PermissionStatus }> => {
    return new Promise<{ canAskAgain: boolean; permissionStatus: PermissionStatus }>((resolve, reject) => {
        try {
            if (canGetWhetherAppCanAskForContactPermissionAgain()) {
                log.info('Aven Advisor native indicates it can return whether we can ask for contact permission again, so web is setting up to receive that')
                window.receiveCanAskForContactPermissionAgain = (payload: { canAskAgain: boolean; permissionStatus: PermissionStatus }) => {
                    log.info('Aven Advisor web received whether it can ask for contact permission again from native: %{payloadJson}', { payloadJson: JSON.stringify(payload) })
                    resolve(payload)
                }
                sendMessageToNativeApp({
                    eventName: WebViewMessageEventName.CAN_ASK_FOR_CONTACT_PERMISSION_AGAIN,
                    windowCallbackMethodName: 'receiveCanAskForContactPermissionAgain',
                })
            } else {
                log.info('Aven Advisor native indicates it cannot return whether we can ask for push permission again, so web is using the default value of true')
                resolve({ canAskAgain: true, permissionStatus: PermissionStatus.UNDETERMINED })
            }
        } catch (e) {
            reject(e)
        }
    }).finally(() => {
        window.receiveCanAskForContactPermissionAgain = undefined
    })
}

export const openExternalWebView = async (url: string, title?: string): Promise<void> => {
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.OPEN_EXTERNAL_WEB_VIEW,
        payload: {
            url,
            title: title ?? '',
        },
    })
}

export const closeExternalWebView = async (): Promise<void> => {
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.CLOSE_EXTERNAL_WEB_VIEW,
    })
}

export const openShareSheet = async (shareContent: { title?: string | undefined; message: string } | { title?: string | undefined; url: string }): Promise<void> => {
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.OPEN_SHARE_SHEET,
        payload: {
            shareContent,
        },
    })
}

// https://docs.expo.dev/versions/latest/sdk/sms/#smsoptions
export const openSMSShareSheet = async (shareContent: { addresses: string[]; message: string; options?: object }): Promise<void> => {
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.OPEN_SMS_SHARE_SHEET,
        payload: {
            shareContent,
        },
    })
}

// https://docs.expo.dev/versions/latest/sdk/mail-composer/#types
export const openMailShareSheet = async (shareContent: {
    attachments?: string[]
    bccRecipients?: string[]
    body?: string
    ccRecipients?: string[]
    isHtml?: boolean
    recipients?: string[]
    subject?: string
}): Promise<void> => {
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.OPEN_MAIL_SHARE_SHEET,
        payload: {
            shareContent,
        },
    })
}

export const saveCurrentURLPath = async (path: string | null): Promise<void> => {
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.SAVE_URL_PATH,
        payload: {
            path,
        },
    })
}

export const savePlaidLinkToken = async (linkToken: string | null): Promise<void> => {
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.SAVE_PLAID_LINK_TOKEN,
        payload: {
            linkToken,
        },
    })
}

export const getPlaidLinkToken = async (): Promise<string> => {
    log.info('Getting plaid link token from native storage')
    return new Promise<string>((resolve, reject) => {
        try {
            window.receivePlaidLinkToken = (linkTokenPayload: { linkToken: string }) => {
                log.info('Got plaid link token from native storage: %{linkToken}', { linkToken: linkTokenPayload.linkToken })
                resolve(linkTokenPayload.linkToken)
            }
            sendMessageToNativeApp({
                eventName: WebViewMessageEventName.GET_PLAID_LINK_TOKEN,
                windowCallbackMethodName: 'receivePlaidLinkToken',
            })
        } catch (e) {
            reject(e)
        }
    }).finally(() => {
        window.receivePlaidLinkToken = undefined
    })
}

// Keep in sync with PlaidLinkState in aven_apps/aven_score/src/Redux/reduxSlices.ts
interface PlaidLinkState {
    plaidPublicToken: string | null
    accountId: string | null
    institutionName: string | null
    errorMessage: string | null
}

export const sendPlaidLinkStateToNative = async (linkState: PlaidLinkState): Promise<void> => {
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.SEND_PLAID_LINK_SUCCESS_DATA,
        payload: linkState,
    })
}

export const sendToastMessageToNative = async (message: string): Promise<void> => {
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.TOAST_MESSAGE,
        payload: {
            message,
        },
    })
}

export interface PersistedWebStore {
    plaidLinkPathToReturnTo: Optional<string>
    dismissedMethodFiUpdateBanner: boolean
    dismissedShoppingTabNuxModal: boolean
}

const getPersistedWebStore = async (): Promise<PersistedWebStore> => {
    log.info('Getting persistedWebStore from native')
    return new Promise<PersistedWebStore>((resolve, reject) => {
        try {
            window.receivePersistedWebStore = (payload: PersistedWebStore) => {
                log.info('Got persisted web store: %{payloadJson}', { payloadJson: JSON.stringify(payload) })
                resolve(payload)
            }
            sendMessageToNativeApp({
                eventName: WebViewMessageEventName.GET_PERSISTED_WEB_STORE,
                windowCallbackMethodName: 'receivePersistedWebStore',
            })
        } catch (e) {
            reject(e)
        }
    }).finally(() => {
        window.receivePersistedWebStore = undefined
    })
}

const setPersistedWebStoreValue = async (key: keyof PersistedWebStore, value: string | number | boolean | null): Promise<void> => {
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.SET_PERSISTED_WEB_STORE_VALUE,
        payload: {
            [key]: value,
        },
    })
}

export const SafeAreaBackgroundColor = {
    WHITE: '#ffffff',
    BLACK: '#000000',
} as const

export const setSafeAreaConfig = async (payload: {
    enabled: boolean
    backgroundColor: string
    insetTop?: number | null
    insetBottom?: number | null
    insetLeft?: number | null
    insetRight?: number | null
}): Promise<void> => {
    if (!getIsSetSafeAreaPaddingAvailable()) {
        log.info('setSafeAreaConfig is not available on this version')
        return
    }
    log.info('Aven Advisor web is setting safe area config: %{payloadJson}', { payloadJson: JSON.stringify(payload) })
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.SET_SAFE_AREA_CONFIG,
        payload,
    })
}

export const getPlaidLinkPathToReturnTo = async (): Promise<Optional<string>> => {
    const persistedWebStore = await getPersistedWebStore()
    return persistedWebStore?.plaidLinkPathToReturnTo
}

export const setPlaidLinkPathToReturnTo = async (path: string | null): Promise<void> => {
    if (path) {
        assert(Object.values(appRoutePaths).includes(path), 'Path must be a valid app route path')
    }
    await setPersistedWebStoreValue('plaidLinkPathToReturnTo', path)
}

export const getDismissedMethodFiUpdateBanner = async (): Promise<boolean> => {
    const persistedWebStore = await getPersistedWebStore()
    return persistedWebStore?.dismissedMethodFiUpdateBanner ?? false
}

export const dismissMethodFiUpdateBanner = async (): Promise<void> => {
    await setPersistedWebStoreValue('dismissedMethodFiUpdateBanner', true)
}

export const getDismissedShoppingTabNuxModal = async (): Promise<boolean> => {
    const persistedWebStore = await getPersistedWebStore()
    return persistedWebStore?.dismissedShoppingTabNuxModal ?? false
}

export const dismissShoppingTabNuxModal = async (): Promise<void> => {
    await setPersistedWebStoreValue('dismissedShoppingTabNuxModal', true)
}

export const tellNativeThatWebIsInteractive = async (): Promise<void> => {
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.WEB_IS_INTERACTIVE,
    })
}

export const getExpoUpdateInfo = async (): Promise<{ channel: string | null; runtimeVersion: string | null; updateId: string | null }> => {
    log.info('Getting information about expo update from native')
    return new Promise<{ channel: string | null; runtimeVersion: string | null; updateId: string | null }>((resolve, reject) => {
        try {
            window.receiveExpoUpdateInfo = (pushPayload: { channel: string | null; runtimeVersion: string | null; updateId: string | null }) => {
                log.info('Got information about currently running Expo update: %{pushPayloadJson}', { pushPayloadJson: JSON.stringify(pushPayload) })
                resolve(pushPayload)
            }
            sendMessageToNativeApp({
                eventName: WebViewMessageEventName.GET_EXPO_UPDATE_INFO,
                windowCallbackMethodName: 'receiveExpoUpdateInfo',
            })
        } catch (e) {
            reject(e)
        }
    }).finally(() => {
        window.receiveExpoUpdateInfo = undefined
    })
}

export const openAppSettings = async (): Promise<void> => {
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.OPEN_APP_SETTINGS,
    })
}

// This is used to notify the native app that the web app is loaded and ready to be used - this is important in determining how deeplinks are handled
export const sendVueLoadedMessage = (): void => {
    if (!canSendVueLoadedMessage()) {
        log.info('Aven Advisor web is calling sendVueLoadedMessage, but it is not an available feature')
        return
    }

    log.info('Aven Advisor web is calling sendVueLoadedMessage')
    sendMessageToNativeApp({
        eventName: WebViewMessageEventName.VUE_LOADED,
    })
}
